import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import * as React from 'react';

import { CatalogAdminEditionClientProvider } from '@/api/catalog-client-context';
import { HttpCatalogAdminEditionClient } from '@/api/catalog-edition-client.http';
import { useCatalogVersions } from '@/api/use-catalog';
import {
  CatalogEditionScreen,
  type CatalogMatrixView,
} from '@/routes/admin/catalog/catalog-edition-screen';

// =============================================================================
// catalog-edition-route.tsx — Adaptateur routeur de l'édition A-09 + matrice (§2.4).
// =============================================================================
// Route `/admin/catalog/versions/$catalogVersionId/edit` (MONDE ADMIN d'ÉCRITURE, P-05,
// distincte des lectures S8). Le BC catalog AUTO-PORTE son DI seam d'écriture : la route
// enveloppe l'écran avec le CatalogAdminEditionClientProvider et injecte l'adaptateur HTTP
// réel (DEC-S20-B2) — sinon l'écran d'écriture défautait sur le stub in-memory.
//
// La version éditée est la RESSOURCE D'URL ($catalogVersionId) ; l'autorité (inv.2 édition
// réservée DRAFT) reste l'API. L'ÉTAT DE VUE de la matrice (famille / actives / heatmap /
// focus) est porté par l'URL (search params TanStack ; RULE_REF_006 : jamais localStorage) :
// l'adaptateur lit l'URL (`useSearch`) et la met à jour (`useNavigate`) ; l'écran reste
// CONTRÔLÉ et testable hors routeur.

const catalogEditionClient = new HttpCatalogAdminEditionClient();

const EDITION_ROUTE_PATH = '/admin/catalog/versions/$catalogVersionId/edit';

/**
 * Valide/normalise les search params de l'état de vue de la matrice. Une valeur absente ou
 * invalide n'est PAS sérialisée (URL propre). Aucune règle métier : simple garde de type.
 * Les booléens sont portés par leur présence (`actives`/`heatmap` = true).
 */
export function validateCatalogEditionSearch(
  search: Record<string, unknown>,
): CatalogMatrixView {
  const result: {
    famille?: string;
    actives?: boolean;
    heatmap?: boolean;
    focus?: string;
  } = {};
  const famille = search['famille'];
  if (typeof famille === 'string' && famille.length > 0) {
    result.famille = famille;
  }
  if (search['actives'] === true || search['actives'] === 'true') {
    result.actives = true;
  }
  if (search['heatmap'] === true || search['heatmap'] === 'true') {
    result.heatmap = true;
  }
  const focus = search['focus'];
  if (typeof focus === 'string' && focus.length > 0) {
    result.focus = focus;
  }
  return result;
}

export function CatalogEditionRoute(): React.JSX.Element {
  const { catalogVersionId } = useParams({ from: EDITION_ROUTE_PATH });
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CatalogMatrixView;

  // État de la version éditée (A-08, browser des versions) : gouverne l'action d'ARCHIVAGE de
  // profil (bouton proposé uniquement sur une version DRAFT ; autorité finale = API). `undefined`
  // tant que la liste charge ⇒ l'écran masque l'action par prudence.
  const versions = useCatalogVersions();
  const versionState = versions.data?.find(
    (v) => v.catalogVersionId === catalogVersionId,
  )?.state;

  const onMatrixViewChange = React.useCallback(
    (next: CatalogMatrixView): void => {
      void navigate({
        to: EDITION_ROUTE_PATH,
        params: { catalogVersionId },
        search: next,
      });
    },
    [navigate, catalogVersionId],
  );

  return (
    <CatalogAdminEditionClientProvider client={catalogEditionClient}>
      <CatalogEditionScreen
        catalogVersionId={catalogVersionId}
        versionState={versionState}
        matrixView={search}
        onMatrixViewChange={onMatrixViewChange}
      />
    </CatalogAdminEditionClientProvider>
  );
}
